import { postSaveData } from '@api/data';
import {
    Button,
    Icon,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Stack,
    useDisclosure,
    Text,
} from '@chakra-ui/react';
import BigSectionTitleComponent from '@components/BigSectionTitleComponent/BigSectionTitleComponent';
import CheckboxComponent from '@components/CheckboxComponent/CheckboxComponent';
import DatePickerComponent from '@components/DatePickerComponent/DatePickerComponent';
import FooterComponent from '@components/FooterComponent/FooterComponent';
import HeaderComponent from '@components/HeaderComponent/HeaderComponent';
import InputComponent from '@components/InputComponent/InputComponent';
import LoaderModal from '@components/LoaderModal/LoaderModal';
import PaymentMethodForm from '@components/PaymentMethodForm/PaymentMethodForm';
import RadioComponent from '@components/RadioComponent/RadioComponent';
import SelectComponent from '@components/SelectComponent/SelectComponent';
import SelectInputComponent from '@components/SelectInputComponent/SelectInputComponent';
import SmallSectionTitleComponent from '@components/SmallSectionTitleComponent/SmallSectionTitleComponent';
import UploadComponent from '@components/UploadComponent/UploadComponent';
import VatManualValidation from '@components/VatManualValidation';
import { StoreContext } from '@contexts/StoreContext';
import BackgroundImage from '@resources/images/providers-banner.jpg';
import { AppRoutes } from '@routes/appRoutes';
import palette from '@theme/assets/palette';
import { DEFAULT_EXTRA_UE_VAT, NOTE_EU_VAT } from '@utils/constants';
import { SupplierNation } from '@utils/enum/SupplierNation';
import { SupplierTypesTag } from '@utils/enum/SupplierTypesTag';
import {
    fileToBase64,
    getApplicantInfoFromAccount,
    validateEmail,
    validateNonSpecialString,
    validatePec,
    validatePhone,
} from '@utils/functions';
import { countries, currencies, euCountries, extraEuCountries, prefixes, provinces } from '@utils/models/DataSelect';
import {
    validatorBeb,
    validatorCollaborators,
    validatorFlightCompany,
    validatorHotel,
    validatorOrdinary,
    validatorRent,
    validatorShipCompany,
    validatorTourOperator,
    validatorTrainCompany,
    voidSupplierForm,
    isSupplierTaxValidationSuccessful,
    isSupplierVatRequired,
} from '@utils/models/SupplierForms';
import { FC, createRef, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useForm } from 'react-hook-form';
import { MdDownload } from 'react-icons/md';
import { useNavigate, useParams } from 'react-router-dom';
import { SupplierItem } from 'types/data';
import { BankTransfer, CreditCard, FieldsChecked, FileData, SupplierForm, TravelAccount } from 'types/formData';
import { ComboInputObject, RadioItem, SupplierFormObjectValidator } from 'types/formElement';
import { StoreContextType } from 'types/reducers';
import { useMsal } from '@azure/msal-react';

const SupplierFormPage: FC = () => {
    const recaptchaRef = createRef<ReCAPTCHA>();
    const params = useParams();
    const navigate = useNavigate();
    const {
        handleSubmit,
        register,
        control,
        trigger,
        watch,
        setValue,
        clearErrors,
        setError,
        formState: { errors },
    } = useForm({ mode: 'onBlur' });
    const { state } = useContext<StoreContextType>(StoreContext);
    const { accounts } = useMsal();
    const hasPrefilledApplicant = useRef(false);
    const [isLoading, setIsLoading] = useState(false);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [supplier, setSupplier] = useState<SupplierItem | null>(null);
    const [formDataSupplier, setFormDataSupplier] = useState<SupplierForm | null>(null);
    const [selectedBilling, setSelectedBilling] = useState<string>('');
    const [vatValidation, setVatValidation] = useState<string[]>([]);
    const [taxIdCodeValidation, setTaxIdCodeValidation] = useState<string[]>([]);
    const [pecValidation, setPecValidation] = useState<string[]>([]);
    const [vatManualValidationStatus, setVatManualValidationStatus] = useState<boolean | null>(null);

    const switchFormDataSupplier = useCallback((supplierFound: SupplierItem) => {
        let validator: SupplierFormObjectValidator | undefined;
        switch (supplierFound.tag) {
            case 'HOTEL':
                validator = validatorHotel(2, supplierFound.id);
                break;
            case 'RENTALS':
                validator = validatorRent(2, supplierFound.id);
                break;
            case 'AIRLINE_COMPANIES':
                validator = validatorFlightCompany(2, supplierFound.id);
                break;
            case 'SHIPPING_COMPANIES':
                validator = validatorShipCompany(2, supplierFound.id);
                break;
            case 'RAILWAY_COMPANIES':
                validator = validatorTrainCompany(2, supplierFound.id);
                break;
            case 'ORDINARY':
                validator = validatorOrdinary(2, supplierFound.id);
                break;
            case 'TOUR_OPERATORS':
                validator = validatorTourOperator(2, supplierFound.id);
                break;
            case 'COLLABORATORS_WITHHOLDING_TAX':
                validator = validatorCollaborators(2, supplierFound.id);
                break;
            case 'BED_AND_BREAKFAST':
                validator = validatorBeb(2, supplierFound.id);
                break;
        }
        if (validator) {
            setFormDataSupplier(voidSupplierForm(validator));
        }
    }, []);

    useEffect(() => {
        if (state.supplierItems.length > 0) {
            const tag = params.tag;
            if (tag) {
                const supplierFound = state.supplierItems.find((item) => item.tag.toLowerCase() === tag);
                if (supplierFound) {
                    setSupplier(supplierFound);
                    switchFormDataSupplier(supplierFound);
                } else {
                    navigate(AppRoutes.HOME_PAGE);
                }
            } else {
                navigate(AppRoutes.HOME_PAGE);
            }
        }
    }, [navigate, params.id, params.tag, state.supplierItems, switchFormDataSupplier]);

    // Prefill "Richiedente" (name/surname/email) from the logged-in Entra account — runs once
    // the void supplier form is ready, never overwrites fields already filled in.
    // The inputs are RHF-registered (uncontrolled via `register`'s ref), so updating
    // formDataSupplier alone does not repaint the DOM value — `setValue` is required too.
    useEffect(() => {
        const account = accounts[0];
        if (hasPrefilledApplicant.current || !account || !formDataSupplier) {
            return;
        }
        hasPrefilledApplicant.current = true;

        const applicantInfo = getApplicantInfoFromAccount(account);
        setFormDataSupplier((prev) => {
            if (!prev) {
                return prev;
            }
            const next = {
                ...prev,
                applicantName: prev.applicantName || applicantInfo.applicantName,
                applicantSurname: prev.applicantSurname || applicantInfo.applicantSurname,
                applicantEmail: prev.applicantEmail || applicantInfo.applicantEmail,
            };
            setValue('applicantName', next.applicantName);
            setValue('applicantSurname', next.applicantSurname);
            setValue('applicantEmail', next.applicantEmail);
            return next;
        });
    }, [accounts, formDataSupplier, setValue]);

    const supplierNationsRadioItems: RadioItem[] = useMemo(() => {
        return [
            {
                id: SupplierNation.ITALY,
                label: 'Italiana',
            },
            {
                id: SupplierNation.EU_COUNTRY,
                label: 'Paese UE',
            },
            {
                id: SupplierNation.EXTRA_EU_COUNTRY,
                label: 'Paese Extra UE',
            },
        ];
    }, []);

    const billingRadioItems: RadioItem[] = useMemo(() => {
        return [
            {
                id: 'sdi',
                label: 'SDI',
            },
            {
                id: 'pec',
                label: 'PEC',
            },
        ];
    }, []);

    const booleanRadioOptions: RadioItem[] = useMemo(() => {
        return [
            {
                id: 'true',
                label: 'Sì',
            },
            {
                id: 'false',
                label: 'No',
            },
        ];
    }, []);

    const onSubmit = useCallback(() => {
        // Check country
        if (recaptchaRef && recaptchaRef.current) {
            const data = {
                ...formDataSupplier,
                legalAddress: {
                    ...formDataSupplier?.legalAddress,
                    country:
                        formDataSupplier?.legalAddress.country === '' && formDataSupplier.supplierNation === 'italy'
                            ? 'IT'
                            : formDataSupplier?.legalAddress.country,
                },
                operationalAddress: {
                    ...formDataSupplier?.operationalAddress,
                    country:
                        formDataSupplier?.operationalAddress?.country === '' &&
                        formDataSupplier.supplierNation === 'italy'
                            ? 'IT'
                            : formDataSupplier?.operationalAddress?.country,
                },
            };
            if (data.birthDate) {
                const formattedDate = data.birthDate.split('/');
                formattedDate.reverse();
                data.birthDate = formattedDate.join('-');
            }
            setIsLoading(true);
            const cleanedData = { ...data };
            delete cleanedData.validations;
            postSaveData(cleanedData, recaptchaRef.current)
                .then(() => {
                    setIsLoading(false);
                    onOpen();
                })
                .catch((error) => {
                    setIsLoading(false);
                    console.log(error);
                });
        }
    }, [formDataSupplier, onOpen, recaptchaRef]);

    const handleChangeApplicantName = useCallback(
        (value: string) => {
            if (formDataSupplier) {
                setFormDataSupplier({ ...formDataSupplier, applicantName: value });
            }
        },
        [formDataSupplier, setFormDataSupplier],
    );

    const handleChangeApplicantSurname = useCallback(
        (value: string) => {
            if (formDataSupplier) {
                setFormDataSupplier({ ...formDataSupplier, applicantSurname: value });
            }
        },
        [formDataSupplier, setFormDataSupplier],
    );

    const handleChangeApplicantEmail = useCallback(
        (value: string) => {
            if (formDataSupplier) {
                setFormDataSupplier({ ...formDataSupplier, applicantEmail: value });
            }
        },
        [formDataSupplier, setFormDataSupplier],
    );

    const handleChangeCompanyName = useCallback(
        (value: string) => {
            if (formDataSupplier) {
                setFormDataSupplier({ ...formDataSupplier, companyName: value });
            }
        },
        [formDataSupplier, setFormDataSupplier],
    );

    const handleChangeCommonName = useCallback(
        (value: string) => {
            if (formDataSupplier) {
                setFormDataSupplier({ ...formDataSupplier, commonName: value });
            }
        },
        [formDataSupplier, setFormDataSupplier],
    );

    const handleSetSupplierNation = useCallback(
        (selected: string) => {
            if (selected && formDataSupplier) {
                setFormDataSupplier({
                    ...formDataSupplier,
                    legalAddress: {
                        ...formDataSupplier.legalAddress,
                        province: '',
                        country: '',
                    },
                    countryTax: selected === SupplierNation.ITALY ? 'IT' : '',
                    operationalAddress: formDataSupplier.operationalAddress
                        ? { ...formDataSupplier.operationalAddress, province: '', country: '' }
                        : null,
                    sdi: null,
                    pec: null,
                    supplierNation: selected,
                    vat: selected === SupplierNation.EXTRA_EU_COUNTRY ? DEFAULT_EXTRA_UE_VAT : '',
                });
                setSelectedBilling('');
                setValue('vat', selected === SupplierNation.EXTRA_EU_COUNTRY ? DEFAULT_EXTRA_UE_VAT : '');
                if (selected === SupplierNation.ITALY) {
                    clearErrors('vat');
                }
                if (selected === SupplierNation.EXTRA_EU_COUNTRY) {
                    setVatManualValidationStatus(true);
                } else {
                    setVatManualValidationStatus(null);
                }
            }
        },
        [clearErrors, setFormDataSupplier, formDataSupplier, setValue],
    );

    const handleSetSpecialCustomsArrangements = useCallback(() => {
        if (formDataSupplier) {
            setFormDataSupplier({
                ...formDataSupplier,
                specialCustomsArrangements: !formDataSupplier.specialCustomsArrangements,
            });
        }
    }, [formDataSupplier]);

    const handleChangeVatNumber = useCallback(
        (value: string) => {
            if (formDataSupplier?.supplierNation !== SupplierNation.EXTRA_EU_COUNTRY) {
                setVatManualValidationStatus(null);
            }
            if (formDataSupplier) {
                setFormDataSupplier({ ...formDataSupplier, vat: value });
                if (!value && formDataSupplier.supplierNation === SupplierNation.ITALY) {
                    clearErrors('vat');
                }
            }
        },
        [clearErrors, formDataSupplier, setFormDataSupplier],
    );

    const handleChangeTaxIdCodeNumber = useCallback(
        (value: string) => {
            if (formDataSupplier?.supplierNation !== SupplierNation.EXTRA_EU_COUNTRY) {
                setVatManualValidationStatus(null);
            }
            if (formDataSupplier) {
                setFormDataSupplier({ ...formDataSupplier, taxIdCode: value });
            }
        },
        [formDataSupplier, setFormDataSupplier],
    );

    const handleChangeCompanyCode = useCallback(
        (value: string) => {
            if (formDataSupplier) {
                setFormDataSupplier({
                    ...formDataSupplier,
                    companyCode: value,
                });
            }
        },
        [formDataSupplier, setFormDataSupplier],
    );

    const handleChangeIcao = useCallback(
        (value: string) => {
            if (formDataSupplier) {
                setFormDataSupplier({
                    ...formDataSupplier,
                    icao: value,
                });
            }
        },
        [formDataSupplier, setFormDataSupplier],
    );

    const handleChangeLegalAddress = useCallback(
        (value: string, key: 'address' | 'city' | 'zip' | 'province' | 'buildingNumber' | 'country') => {
            if (key && formDataSupplier) {
                if (key === 'country') {
                    setFormDataSupplier({
                        ...formDataSupplier,
                        countryTax: value,
                    });
                }
                setFormDataSupplier({
                    ...formDataSupplier,
                    legalAddress: {
                        ...formDataSupplier?.legalAddress,
                        [key]: value,
                    },
                });
            }
        },
        [formDataSupplier, setFormDataSupplier],
    );

    const handleChangeOperationalAddress = useCallback(
        (value: string, key: 'address' | 'city' | 'zip' | 'province' | 'buildingNumber' | 'country') => {
            if (key && formDataSupplier && formDataSupplier.operationalAddress) {
                setFormDataSupplier({
                    ...formDataSupplier,
                    operationalAddress: {
                        ...formDataSupplier?.operationalAddress,
                        [key]: value,
                    },
                });
            }
        },
        [formDataSupplier, setFormDataSupplier],
    );

    const handleChangePhoneNumber = useCallback(
        (values: ComboInputObject) => {
            if ((values.inputValue || values.selectValue) && formDataSupplier && formDataSupplier.contact.phoneNumber) {
                const foundPrefix = prefixes.find((prefix) => prefix.id === values.selectValue.toString());
                setFormDataSupplier({
                    ...formDataSupplier,
                    contact: {
                        ...formDataSupplier?.contact,
                        phoneNumber: {
                            prefix: foundPrefix ? foundPrefix.label : '',
                            number: values.inputValue,
                        },
                    },
                });
            }
        },
        [formDataSupplier],
    );

    const handleChangeEmail = useCallback(
        (value: string) => {
            if (formDataSupplier) {
                setFormDataSupplier({
                    ...formDataSupplier,
                    contact: {
                        ...formDataSupplier.contact,
                        email: value,
                    },
                });
            }
        },
        [formDataSupplier, setFormDataSupplier],
    );

    const handleChangePaymentCountryCode = useCallback(
        (code: string) => {
            if (code && formDataSupplier) {
                setFormDataSupplier({
                    ...formDataSupplier,
                    paymentMethod: {
                        ...formDataSupplier?.paymentMethod,
                        countryCode: code,
                    },
                });
            }
        },
        [formDataSupplier, setFormDataSupplier],
    );

    const handleChangePaymentCurrencyCode = useCallback(
        (code: string) => {
            if (code && formDataSupplier) {
                setFormDataSupplier({
                    ...formDataSupplier,
                    paymentMethod: {
                        ...formDataSupplier?.paymentMethod,
                        currencyCode: code,
                    },
                });
            }
        },
        [formDataSupplier, setFormDataSupplier],
    );

    const handlePaymentMethodId = useCallback(
        (id: number, object: CreditCard | BankTransfer | TravelAccount | null) => {
            if (formDataSupplier && state.paymentType) {
                setFormDataSupplier({
                    ...formDataSupplier,
                    paymentMethod: {
                        ...formDataSupplier.paymentMethod,
                        paymentMethodId: id,
                        paymentData: object,
                    },
                });
            }
        },
        [formDataSupplier, state.paymentType],
    );

    const handleUpdatePaymentMethod = useCallback(
        (data: CreditCard | BankTransfer | TravelAccount | null) => {
            if (formDataSupplier && formDataSupplier.paymentMethod.paymentData) {
                setFormDataSupplier({
                    ...formDataSupplier,
                    paymentMethod: {
                        ...formDataSupplier.paymentMethod,
                        paymentData: data,
                    },
                });
            }
        },
        [formDataSupplier, setFormDataSupplier],
    );

    const handleSetSelectedBilling = useCallback(
        (selected: string | number) => {
            if (formDataSupplier) {
                setFormDataSupplier({
                    ...formDataSupplier,
                    sdi: selected === 'sdi' ? '' : null,
                    pec: selected === 'pec' ? '' : null,
                });
                setSelectedBilling(selected as string);
            }
        },
        [formDataSupplier, setSelectedBilling],
    );

    const handleChangeSdi = useCallback(
        (value: string) => {
            if (formDataSupplier) {
                setFormDataSupplier({
                    ...formDataSupplier,
                    sdi: value,
                });
            }
        },
        [formDataSupplier, setFormDataSupplier],
    );

    const handleChangePec = useCallback(
        (value: string) => {
            if (formDataSupplier) {
                setFormDataSupplier({
                    ...formDataSupplier,
                    pec: value,
                });
            }
        },
        [formDataSupplier, setFormDataSupplier],
    );

    const handleCountryTax = useCallback(
        (value: string) => {
            if (formDataSupplier) {
                if (formDataSupplier?.supplierNation !== SupplierNation.EXTRA_EU_COUNTRY) {
                    setVatManualValidationStatus(null);
                }
                setFormDataSupplier({
                    ...formDataSupplier,
                    countryTax: value,
                });
            }
        },
        [formDataSupplier],
    );

    const handleBirthDate = useCallback(
        (value: string) => {
            if (formDataSupplier) {
                setFormDataSupplier({
                    ...formDataSupplier,
                    birthDate: value,
                });
            }
        },
        [formDataSupplier],
    );

    const handleBirthPlace = useCallback(
        (value: string) => {
            if (formDataSupplier) {
                setFormDataSupplier({
                    ...formDataSupplier,
                    birthPlace: value,
                });
            }
        },
        [formDataSupplier],
    );

    const handleSetInvoiceWorker = useCallback(
        (selected: string | number) => {
            if (formDataSupplier) {
                setFormDataSupplier({
                    ...formDataSupplier,
                    invoiceWorker: selected === 'true' ? true : false,
                });
            }
        },
        [formDataSupplier],
    );

    const isRequiredVat = isSupplierVatRequired(formDataSupplier?.supplierNation, supplier?.tag);

    const isRequiredOperationalAddress = useMemo(() => {
        if (supplier) {
            switch (supplier.tag) {
                case SupplierTypesTag.HOTEL:
                    return true;
                default:
                    return false;
            }
        }
        return false;
    }, [supplier]);

    const isRequiredCommonName = useMemo(() => {
        if (supplier) {
            switch (supplier.tag) {
                case SupplierTypesTag.HOTEL:
                case SupplierTypesTag.RENTALS:
                case SupplierTypesTag.COLLABORATORS_WITHHOLDING_TAX:
                case SupplierTypesTag.BED_AND_BREAKFAST:
                    return true;
                default:
                    return false;
            }
        }
        return false;
    }, [supplier]);

    const isRequiredEmail = useMemo(() => {
        if (supplier) {
            switch (supplier.tag) {
                case SupplierTypesTag.HOTEL:
                case SupplierTypesTag.BED_AND_BREAKFAST:
                    return true;
                default:
                    return false;
            }
        }
        return false;
    }, [supplier]);

    const isRequiredPhone = useMemo(() => {
        if (supplier) {
            switch (supplier.tag) {
                case SupplierTypesTag.HOTEL:
                case SupplierTypesTag.SHIPPING_COMPANIES:
                case SupplierTypesTag.TOUR_OPERATORS:
                case SupplierTypesTag.BED_AND_BREAKFAST:
                    return true;
                default:
                    return false;
            }
        }
        return false;
    }, [supplier]);

    const vatLength = useMemo(() => {
        if (formDataSupplier?.supplierNation) {
            if (formDataSupplier?.supplierNation === SupplierNation.ITALY) {
                return 11;
            } else {
                return undefined;
            }
        }
        return 0;
    }, [formDataSupplier?.supplierNation]);

    const stateSelection = useMemo(() => {
        if (formDataSupplier?.supplierNation === SupplierNation.EU_COUNTRY) {
            return (
                <SelectComponent
                    options={euCountries}
                    valueComponent={formDataSupplier.countryTax}
                    onChangeComponent={(value) => handleCountryTax(value as string)}
                    control={control}
                    id="euCountryTaxSelect"
                    label="Nazione società"
                    isRequired
                    isInvalid={errors.euCountryTaxSelect ?? null}
                    flex={1}
                />
            );
        }
        if (formDataSupplier?.supplierNation === SupplierNation.EXTRA_EU_COUNTRY) {
            return (
                <SelectComponent
                    options={extraEuCountries}
                    valueComponent={formDataSupplier.countryTax}
                    onChangeComponent={(value) => handleCountryTax(value as string)}
                    control={control}
                    id="extraEuCountryTaxSelect"
                    label="Nazione società"
                    isRequired
                    isInvalid={errors.extraEuCountryTaxSelect ?? null}
                    flex={1}
                />
            );
        }
        return false;
    }, [formDataSupplier, control, errors.euCountryTaxSelect, errors.extraEuCountryTaxSelect]);

    const disableTaxInput = useMemo(() => {
        if (
            formDataSupplier?.supplierNation === SupplierNation.ITALY ||
            (formDataSupplier?.supplierNation !== SupplierNation.ITALY && formDataSupplier?.countryTax)
        ) {
            return false;
        }
        return true;
    }, [formDataSupplier?.supplierNation, formDataSupplier?.countryTax]);

    const travelCompanyRow = useMemo(() => {
        if (supplier && formDataSupplier) {
            if (
                supplier.tag === SupplierTypesTag.AIRLINE_COMPANIES ||
                supplier.tag === SupplierTypesTag.SHIPPING_COMPANIES ||
                supplier.tag === SupplierTypesTag.RAILWAY_COMPANIES
            ) {
                return (
                    <Stack direction="row" justify="center" align="center" spacing="30px" alignSelf="stretch">
                        <InputComponent
                            id="companyCode"
                            label="Codice compagnia"
                            value={formDataSupplier.companyCode ?? ''}
                            onChange={handleChangeCompanyCode}
                            register={register}
                            isRequired={supplier.tag === SupplierTypesTag.AIRLINE_COMPANIES}
                            isInvalid={errors.companyCode ?? null}
                        />
                        {supplier.tag === SupplierTypesTag.AIRLINE_COMPANIES ? (
                            <InputComponent
                                id={'icao'}
                                label="ICAO"
                                value={formDataSupplier.icao ?? ''}
                                onChange={handleChangeIcao}
                                register={register}
                                isRequired
                                isInvalid={errors.icao ?? null}
                                maxLength={4}
                            />
                        ) : (
                            <Stack flex={1} />
                        )}
                    </Stack>
                );
            }
        }
        return null;
    }, [
        supplier,
        formDataSupplier,
        handleChangeCompanyCode,
        register,
        errors.companyCode,
        errors.icao,
        handleChangeIcao,
    ]);

    const legalAddressSelectComponent = useMemo(() => {
        if (supplier && formDataSupplier) {
            if (formDataSupplier?.supplierNation === SupplierNation.ITALY) {
                return (
                    <SelectComponent
                        id="legalAddressProvince"
                        label="Provincia"
                        valueComponent={formDataSupplier.legalAddress.province}
                        options={provinces}
                        onChangeComponent={(value) => {
                            handleChangeLegalAddress(value as string, 'province');
                        }}
                        flex={1}
                        isRequired
                        isInvalid={errors.legalAddressProvince ?? null}
                        control={control}
                    />
                );
            } else {
                const countriesToSelect =
                    formDataSupplier?.supplierNation === SupplierNation.EU_COUNTRY ? euCountries : extraEuCountries;
                return (
                    <SelectComponent
                        id="legalAddressCountry"
                        label="Nazione"
                        valueComponent={formDataSupplier.legalAddress.country}
                        options={countriesToSelect}
                        onChangeComponent={(value) => {
                            handleChangeLegalAddress(value as string, 'country');
                        }}
                        flex={1}
                        isRequired
                        isInvalid={errors.legalAddressCountry ?? null}
                        control={control}
                    />
                );
            }
        }
        return null;
    }, [
        supplier,
        formDataSupplier,
        formDataSupplier?.supplierNation,
        errors.legalAddressProvince,
        errors.legalAddressCountry,
        control,
        handleChangeLegalAddress,
    ]);

    const operationalAddressSelectComponent = useMemo(() => {
        if (supplier && formDataSupplier && formDataSupplier.operationalAddress) {
            if (formDataSupplier?.supplierNation === SupplierNation.ITALY) {
                return (
                    <SelectComponent
                        id="operationalAddressProvince"
                        label="Provincia"
                        valueComponent={formDataSupplier.operationalAddress.province}
                        options={provinces}
                        onChangeComponent={(value) => {
                            handleChangeOperationalAddress(value as string, 'province');
                        }}
                        flex={1}
                        isRequired
                        isInvalid={errors.operationalAddressProvince ?? null}
                        control={control}
                    />
                );
            } else {
                const countriesToSelect =
                    formDataSupplier?.supplierNation === SupplierNation.EU_COUNTRY ? euCountries : extraEuCountries;
                return (
                    <SelectComponent
                        id="operationalAddressCountry"
                        label="Nazione"
                        valueComponent={formDataSupplier.operationalAddress.country}
                        options={countriesToSelect}
                        onChangeComponent={(value) => {
                            handleChangeOperationalAddress(value as string, 'country');
                        }}
                        flex={1}
                        isRequired
                        isInvalid={errors.operationalAddressCountry ?? null}
                        control={control}
                    />
                );
            }
        }
        return null;
    }, [
        supplier,
        formDataSupplier,
        formDataSupplier?.supplierNation,
        errors.operationalAddressProvince,
        errors.operationalAddressCountry,
        control,
        handleChangeOperationalAddress,
    ]);

    const handleUpdateInvoiceWorkerAttachment = useCallback(
        async (files: File[]) => {
            if (files) {
                const typeId = state.attachmentTypes.find((at) => at.tag === 'SELF_EMPLOYEED')?.id ?? 0;

                const filestream = (await fileToBase64(files[0])) as string;
                const name = files[0].name;
                const size = files[0].size;
                const mimeType = files[0].type;
                const attachment: FileData = {
                    filestream: filestream.split(',')[1],
                    name,
                    size,
                    mimeType,
                    typeId,
                };
                if (formDataSupplier) {
                    setFormDataSupplier({
                        ...formDataSupplier,
                        invoiceWorkerAttachment: attachment,
                    });
                }
            }
        },
        [formDataSupplier, state.attachmentTypes],
    );

    const handleDeleteInvoiceWorkerAttachment = useCallback(() => {
        if (formDataSupplier) {
            setFormDataSupplier({
                ...formDataSupplier,
                invoiceWorkerAttachment: null,
            });
        }
    }, [formDataSupplier]);

    const billingData = useMemo(() => {
        if (formDataSupplier && supplier) {
            if (
                formDataSupplier?.supplierNation === SupplierNation.ITALY ||
                supplier.tag === SupplierTypesTag.COLLABORATORS_WITHHOLDING_TAX
            ) {
                return (
                    <>
                        <BigSectionTitleComponent title="Fatturazione" />
                        {supplier.tag === SupplierTypesTag.COLLABORATORS_WITHHOLDING_TAX ? (
                            <RadioComponent
                                items={booleanRadioOptions}
                                id="invoiceWorker"
                                register={register}
                                name="Fattura per lavoratore autonomo"
                                onChange={handleSetInvoiceWorker}
                                selected={formDataSupplier.invoiceWorker ? 'true' : 'false'}
                                direction="column"
                            />
                        ) : null}
                        {supplier.tag === SupplierTypesTag.COLLABORATORS_WITHHOLDING_TAX &&
                        formDataSupplier.invoiceWorker ? (
                            <UploadComponent
                                id="invoiceWorkerAttachment"
                                title="Fattura"
                                subtitle="Carica la fattura:"
                                uploadedFile={formDataSupplier.invoiceWorkerAttachment}
                                updateFile={handleUpdateInvoiceWorkerAttachment}
                                deleteFile={handleDeleteInvoiceWorkerAttachment}
                                control={control}
                                error={errors.invoiceWorkerAttachment ?? null}
                                isInvalid={errors.invoiceWorkerAttachment ? true : false}
                                isRequired
                                maxSize={1048576}
                                sizeString="1 MB"
                            />
                        ) : null}
                        {formDataSupplier?.supplierNation === SupplierNation.ITALY ? (
                            <>
                                <RadioComponent
                                    items={billingRadioItems}
                                    id="billingSelect"
                                    register={register}
                                    name="Riferimento fatturazione elettronica"
                                    onChange={handleSetSelectedBilling}
                                    selected={selectedBilling}
                                    direction="column"
                                />
                                {selectedBilling ? (
                                    <Stack
                                        direction="row"
                                        justify="center"
                                        align="center"
                                        spacing="30px"
                                        alignSelf="stretch"
                                    >
                                        {selectedBilling === 'sdi' ? (
                                            <InputComponent
                                                id="sdi"
                                                label="SDI"
                                                register={register}
                                                value={formDataSupplier.sdi ?? ''}
                                                onChange={handleChangeSdi}
                                                isInvalid={errors.sdi ?? null}
                                                minLength={7}
                                                maxLength={7}
                                            />
                                        ) : null}
                                        {selectedBilling === 'pec' ? (
                                            <InputComponent
                                                id="pec"
                                                label="PEC"
                                                register={register}
                                                value={formDataSupplier.pec ?? ''}
                                                onChange={handleChangePec}
                                                isInvalid={errors.pec ?? null}
                                                maxLength={80}
                                                validate={(value) => {
                                                    return conditionalValidation(value, 'pec');
                                                }}
                                            />
                                        ) : null}
                                    </Stack>
                                ) : null}
                            </>
                        ) : null}
                    </>
                );
            }
        }
        return null;
    }, [
        formDataSupplier,
        supplier,
        formDataSupplier?.supplierNation,
        billingRadioItems,
        register,
        handleSetSelectedBilling,
        selectedBilling,
        handleChangeSdi,
        errors.sdi,
        errors.pec,
        handleChangePec,
        errors,
    ]);

    const prefixId: string = useMemo(() => {
        if (formDataSupplier && formDataSupplier.contact.phoneNumber?.prefix) {
            const foundPrefix = prefixes.find(
                (prefix) => prefix.label === formDataSupplier.contact.phoneNumber?.prefix,
            );
            if (foundPrefix) {
                return foundPrefix.id;
            }
        }
        return '';
    }, [formDataSupplier]);

    const conditionalValidation = useCallback(
        async (value: string, field: string) => {
            if (recaptchaRef && recaptchaRef.current) {
                // if (field === 'vat' && !formDataSupplier?.specialCustomsArrangements) {
                //     if (
                //         value !== '' &&
                //         !vatValidation.includes(value) &&
                //         // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                //         ![...(formDataSupplier?.validations?.vatValidations ?? [])].includes(value)
                //     ) {
                //         const valid = await validateVat(
                //             value,
                //             formDataSupplier?.supplierNation === SupplierNation.ITALY
                //                 ? 'IT'
                //                 : (formDataSupplier?.countryTax as string),
                //             recaptchaRef.current,
                //         );
                //         if (valid === true) {
                //             setVatValidation([...vatValidation, value]);
                //         }
                //         return valid;
                //     }
                // }
                // if (field === 'taxIdCode' && !formDataSupplier?.specialCustomsArrangements) {
                //     if (
                //         value !== '' &&
                //         !taxIdCodeValidation.includes(value) &&
                //         // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                //         ![...(formDataSupplier?.validations?.taxIdCodeValidations ?? [])].includes(value)
                //     ) {
                //         const valid = await validateTaxIdCode(
                //             value,
                //             formDataSupplier?.supplierNation === SupplierNation.ITALY
                //                 ? 'IT'
                //                 : (formDataSupplier?.countryTax as string),
                //             recaptchaRef.current,
                //             formDataSupplier?.specialCustomsArrangements,
                //             true,
                //         );
                //         if (valid) {
                //             setTaxIdCodeValidation([...taxIdCodeValidation, value]);
                //         }
                //         return valid;
                //     }
                // }
                if (field === 'pec') {
                    if (
                        value !== '' &&
                        !pecValidation.includes(value) &&
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                        ![...(formDataSupplier?.validations?.pecValidations ?? [])].includes(value)
                    ) {
                        const valid = await validatePec(value, recaptchaRef.current);
                        if (valid) {
                            setPecValidation([...pecValidation, value]);
                        }
                        return valid;
                    }
                }

                return true;
            }
        },
        [formDataSupplier?.validations?.pecValidations, pecValidation, recaptchaRef],
    );

    const checkVatValidation = useCallback(
        (fieldsValidation: FieldsChecked) => {
            if (fieldsValidation.vat === false || typeof fieldsValidation.vat === 'string') {
                setError('vat', { message: fieldsValidation.vat === false ? '' : fieldsValidation.vat });
            }
            if (fieldsValidation.vat === true) {
                setVatValidation([...vatValidation, formDataSupplier?.vat ?? '']);
            }

            if (fieldsValidation.taxIdCode === false || typeof fieldsValidation.taxIdCode === 'string') {
                setError('taxIdCode', {
                    message: fieldsValidation.taxIdCode === false ? '' : fieldsValidation.taxIdCode,
                });
            }
            if (fieldsValidation.taxIdCode === true) {
                setTaxIdCodeValidation([...taxIdCodeValidation, formDataSupplier?.taxIdCode ?? '']);
            }

            if (
                formDataSupplier?.supplierNation &&
                isSupplierTaxValidationSuccessful(formDataSupplier.supplierNation, supplier?.tag, fieldsValidation)
            ) {
                setVatManualValidationStatus(true);
                return;
            }
            setVatManualValidationStatus(false);
        },
        [
            formDataSupplier?.supplierNation,
            formDataSupplier?.taxIdCode,
            formDataSupplier?.vat,
            supplier?.tag,
            setError,
            taxIdCodeValidation,
            vatValidation,
        ],
    );

    const renderVatValidationButton = useMemo(() => {
        if (formDataSupplier) {
            if (
                formDataSupplier?.supplierNation !== SupplierNation.ITALY &&
                formDataSupplier?.specialCustomsArrangements
            ) {
                return null;
            }
            if (formDataSupplier?.supplierNation === SupplierNation.EXTRA_EU_COUNTRY) {
                return null;
            }
            return (
                <VatManualValidation
                    vat={formDataSupplier.vat}
                    vatGroup={null}
                    taxIdCode={formDataSupplier.taxIdCode}
                    specialCustomsArrangements={formDataSupplier.specialCustomsArrangements}
                    country={
                        formDataSupplier?.supplierNation === SupplierNation.ITALY ? 'it' : formDataSupplier.countryTax
                    }
                    status={vatManualValidationStatus}
                    onValidate={checkVatValidation}
                    recaptchaRef={recaptchaRef}
                    requiredTaxIdCode={formDataSupplier?.supplierNation === SupplierNation.ITALY}
                />
            );
        }
        return null;
    }, [checkVatValidation, formDataSupplier, recaptchaRef, vatManualValidationStatus]);

    useEffect(() => {
        if (formDataSupplier?.specialCustomsArrangements && formDataSupplier?.supplierNation !== SupplierNation.ITALY) {
            setVatManualValidationStatus(true);
        } else {
            setVatManualValidationStatus(null);
        }
        // if (formDataSupplier?.vat) {
        //     trigger('vat');
        // }
        // if (formDataSupplier?.taxIdCode) {
        //     trigger('taxIdCode');
        // }
    }, [formDataSupplier?.specialCustomsArrangements]);

    if (supplier) {
        return (
            <Stack minHeight="100vh" backgroundColor="white">
                <HeaderComponent title={supplier?.name ?? ''} goBack backgroundImg={BackgroundImage} />
                {formDataSupplier ? (
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Stack
                            paddingX="20px"
                            paddingTop="20px"
                            paddingBottom="20px"
                            justify="center"
                            align="center"
                            spacing="20px"
                            width="900px"
                            maxWidth="100%"
                            margin="0 auto"
                        >
                            <BigSectionTitleComponent title="Richiedente" />
                            <Stack direction="row" justify="center" align="center" spacing="30px" alignSelf="stretch">
                                <InputComponent
                                    id="applicantName"
                                    label="Nome"
                                    register={register}
                                    value={formDataSupplier.applicantName ?? ''}
                                    onChange={handleChangeApplicantName}
                                    isInvalid={errors.applicantName ?? null}
                                    maxLength={80}
                                    isRequired
                                />
                                <InputComponent
                                    id="applicantSurname"
                                    label="Cognome"
                                    register={register}
                                    value={formDataSupplier.applicantSurname ?? ''}
                                    onChange={handleChangeApplicantSurname}
                                    isInvalid={errors.applicantSurname ?? null}
                                    isRequired
                                    maxLength={80}
                                />
                            </Stack>
                            <Stack direction="row" justify="center" align="center" spacing="30px" alignSelf="stretch">
                                <InputComponent
                                    id="applicantEmail"
                                    label="Email"
                                    register={register}
                                    value={formDataSupplier.applicantEmail ?? ''}
                                    onChange={handleChangeApplicantEmail}
                                    isInvalid={errors.applicantEmail ?? null}
                                    isRequired
                                    type="email"
                                    validate={validateEmail}
                                />
                            </Stack>
                        </Stack>
                        <Stack
                            paddingX="20px"
                            paddingTop="20px"
                            paddingBottom="60px"
                            justify="center"
                            align="center"
                            spacing="20px"
                            width="900px"
                            maxWidth="100%"
                            margin="0 auto"
                        >
                            <BigSectionTitleComponent title="Anagrafica" />
                            <Stack direction="row" justify="center" align="center" spacing="30px" alignSelf="stretch">
                                <InputComponent
                                    id="companyName"
                                    label="Ragione sociale"
                                    register={register}
                                    value={formDataSupplier.companyName}
                                    onChange={handleChangeCompanyName}
                                    isInvalid={errors.companyName ?? null}
                                    minLength={5}
                                    maxLength={80}
                                    isRequired
                                    validate={validateNonSpecialString}
                                />
                                {formDataSupplier.commonName !== null ? (
                                    <InputComponent
                                        id="commonName"
                                        label="Insegna/denominazione"
                                        register={register}
                                        value={formDataSupplier.commonName ?? ''}
                                        onChange={handleChangeCommonName}
                                        isInvalid={errors.commonName ?? null}
                                        isRequired={isRequiredCommonName}
                                        maxLength={36}
                                    />
                                ) : null}
                            </Stack>
                            {formDataSupplier.birthDate !== null || formDataSupplier.birthPlace !== null ? (
                                <Stack
                                    direction="row"
                                    justify="center"
                                    align="center"
                                    spacing="30px"
                                    alignSelf="stretch"
                                >
                                    {formDataSupplier.birthDate !== null ? (
                                        <DatePickerComponent
                                            id={`birthDate`}
                                            label="Data di nascita"
                                            isInvalid={errors.birthDate ?? null}
                                            value={formDataSupplier.birthDate ?? ''}
                                            onChangeProp={handleBirthDate}
                                            isRequired
                                            control={control}
                                            dateFormat="dd/MM/yyyy"
                                            hasMinDate={false}
                                            hasFullWidth
                                        />
                                    ) : null}
                                    {formDataSupplier.birthPlace !== null ? (
                                        <InputComponent
                                            id="birthPlace"
                                            label="Luogo di nascita"
                                            register={register}
                                            value={formDataSupplier.birthPlace ?? ''}
                                            onChange={handleBirthPlace}
                                            isInvalid={errors.birthPlace ?? null}
                                            isRequired
                                            maxLength={36}
                                        />
                                    ) : null}
                                </Stack>
                            ) : null}
                            {travelCompanyRow}
                            <RadioComponent
                                id="supplierNation"
                                register={register}
                                name="Società"
                                items={supplierNationsRadioItems}
                                selected={formDataSupplier?.supplierNation ?? ''}
                                onChange={handleSetSupplierNation}
                            />
                            {formDataSupplier?.supplierNation === SupplierNation.ITALY ||
                            formDataSupplier?.supplierNation === SupplierNation.EU_COUNTRY ? (
                                <CheckboxComponent
                                    id={'specialCustomsArrangements'}
                                    name="Regime doganale speciale"
                                    selected={formDataSupplier.specialCustomsArrangements}
                                    register={register}
                                    onChange={handleSetSpecialCustomsArrangements}
                                />
                            ) : null}
                            {formDataSupplier?.supplierNation === SupplierNation.EU_COUNTRY && (
                                <Text fontStyle="italic" fontSize="14px" color="#999">
                                    {NOTE_EU_VAT}
                                </Text>
                            )}
                            <Stack direction="row" justify="center" align="center" spacing="30px" alignSelf="stretch">
                                {stateSelection}
                                <InputComponent
                                    id="vat"
                                    label="Partita IVA"
                                    register={register}
                                    value={formDataSupplier.vat ?? ''}
                                    onChange={handleChangeVatNumber}
                                    isInvalid={errors.vat ?? null}
                                    isRequired={isRequiredVat}
                                    maxLength={vatLength}
                                    minLength={vatLength}
                                    isDisabled={disableTaxInput}
                                    // validate={(value) => {
                                    //     return conditionalValidation(value, 'vat');
                                    // }}
                                />
                                {formDataSupplier?.supplierNation === SupplierNation.ITALY ? (
                                    <InputComponent
                                        id="taxIdCode"
                                        label="Codice Fiscale"
                                        register={register}
                                        value={formDataSupplier.taxIdCode ?? ''}
                                        onChange={handleChangeTaxIdCodeNumber}
                                        isInvalid={errors.taxIdCode ?? null}
                                        isRequired
                                        minLength={11}
                                        maxLength={16}
                                        isDisabled={!formDataSupplier?.supplierNation}
                                        // validate={(value) => {
                                        //     return conditionalValidation(value, 'taxIdCode');
                                        // }}
                                    />
                                ) : !formDataSupplier?.supplierNation ? (
                                    <Stack flex={1} />
                                ) : null}
                            </Stack>
                            {renderVatValidationButton}
                            <SmallSectionTitleComponent title="Sede legale" />
                            <Stack direction="row" justify="center" align="center" spacing="30px" alignSelf="stretch">
                                <InputComponent
                                    id="legalAddressAddress"
                                    label="Indirizzo sede legale"
                                    register={register}
                                    value={formDataSupplier.legalAddress.address ?? ''}
                                    onChange={(value) => handleChangeLegalAddress(value, 'address')}
                                    isInvalid={errors.legalAddressAddress ?? null}
                                    isRequired
                                    flex={3.4}
                                />
                                <InputComponent
                                    id="legalAddressBuildingNumber"
                                    label="Numero civico"
                                    register={register}
                                    value={formDataSupplier.legalAddress.buildingNumber ?? ''}
                                    onChange={(value) => handleChangeLegalAddress(value, 'buildingNumber')}
                                    isInvalid={errors.legalAddressBuildingNumber ?? null}
                                    isRequired
                                    flex={1}
                                />
                            </Stack>
                            <Stack direction="row" justify="center" align="center" spacing="30px" alignSelf="stretch">
                                <InputComponent
                                    id="legalAddressCity"
                                    label="Città"
                                    register={register}
                                    value={formDataSupplier.legalAddress.city ?? ''}
                                    onChange={(value) => handleChangeLegalAddress(value, 'city')}
                                    isInvalid={errors.legalAddressCity ?? null}
                                    flex={2.2}
                                    isRequired
                                />
                                <InputComponent
                                    id="legalAddressZip"
                                    label="CAP"
                                    register={register}
                                    value={formDataSupplier.legalAddress.zip ?? ''}
                                    onChange={(value) => handleChangeLegalAddress(value, 'zip')}
                                    isInvalid={errors.legalAddressZip ?? null}
                                    flex={1}
                                    minLength={
                                        formDataSupplier?.supplierNation === SupplierNation.ITALY ? 5 : undefined
                                    }
                                    maxLength={
                                        formDataSupplier?.supplierNation === SupplierNation.ITALY ? 5 : undefined
                                    }
                                    isRequired
                                />
                                {legalAddressSelectComponent}
                            </Stack>
                            {formDataSupplier.operationalAddress ? (
                                <>
                                    <SmallSectionTitleComponent title="Sede operativa" />
                                    <Stack
                                        direction="row"
                                        justify="center"
                                        align="center"
                                        spacing="30px"
                                        alignSelf="stretch"
                                    >
                                        <InputComponent
                                            id="operationalAddressAddress"
                                            label="Indirizzo sede operativa"
                                            register={register}
                                            value={formDataSupplier.operationalAddress.address ?? ''}
                                            onChange={(value) => handleChangeOperationalAddress(value, 'address')}
                                            isInvalid={errors.operationalAddressAddress ?? null}
                                            isRequired={isRequiredOperationalAddress}
                                            flex={3.4}
                                        />
                                        <InputComponent
                                            id="operationalAddressBuildingNumber"
                                            label="Numero civico"
                                            register={register}
                                            value={formDataSupplier.operationalAddress.buildingNumber ?? ''}
                                            onChange={(value) =>
                                                handleChangeOperationalAddress(value, 'buildingNumber')
                                            }
                                            isInvalid={errors.operationalAddressBuildingNumber ?? null}
                                            isRequired={isRequiredOperationalAddress}
                                            flex={1}
                                        />
                                    </Stack>
                                    <Stack
                                        direction="row"
                                        justify="center"
                                        align="center"
                                        spacing="30px"
                                        alignSelf="stretch"
                                    >
                                        <InputComponent
                                            id="operationalAddressCity"
                                            label="Città"
                                            register={register}
                                            value={formDataSupplier.operationalAddress.city ?? ''}
                                            onChange={(value) => handleChangeOperationalAddress(value, 'city')}
                                            isInvalid={errors.operationalAddressCity ?? null}
                                            isRequired={isRequiredOperationalAddress}
                                            flex={2.2}
                                        />
                                        <InputComponent
                                            id="operationalAddressZip"
                                            label="CAP"
                                            register={register}
                                            value={formDataSupplier.operationalAddress.zip ?? ''}
                                            onChange={(value) => handleChangeOperationalAddress(value, 'zip')}
                                            isInvalid={errors.operationalAddressZip ?? null}
                                            flex={1}
                                            minLength={
                                                formDataSupplier?.supplierNation === SupplierNation.ITALY
                                                    ? 5
                                                    : undefined
                                            }
                                            maxLength={
                                                formDataSupplier?.supplierNation === SupplierNation.ITALY
                                                    ? 5
                                                    : undefined
                                            }
                                            isRequired={isRequiredOperationalAddress}
                                        />
                                        {operationalAddressSelectComponent}
                                    </Stack>
                                </>
                            ) : null}
                            {formDataSupplier.contact.phoneNumber !== null ||
                            formDataSupplier.contact.email !== null ? (
                                <>
                                    <SmallSectionTitleComponent title="Contatti" />
                                    <Stack
                                        direction="row"
                                        justify="center"
                                        align="center"
                                        spacing="30px"
                                        alignSelf="stretch"
                                    >
                                        {formDataSupplier.contact.phoneNumber !== null ? (
                                            <SelectInputComponent
                                                labelSelect="Prefisso"
                                                labelInput="Telefono"
                                                options={prefixes.map((prefix) => {
                                                    return {
                                                        ...prefix,
                                                        label: `(${prefix.value}) ${prefix.label}`,
                                                    };
                                                })}
                                                value={{
                                                    inputValue: formDataSupplier.contact.phoneNumber?.number ?? '',
                                                    selectValue: prefixId ?? '',
                                                }}
                                                onChange={handleChangePhoneNumber}
                                                registerInput={register}
                                                controlSelect={control}
                                                isInvalidInput={errors.telefono ?? null}
                                                isRequired={isRequiredPhone}
                                                inputType="tel"
                                                maxInputLength={15}
                                                minInputLength={6}
                                                validateInput={validatePhone}
                                                trigger={trigger}
                                                watch={watch}
                                            />
                                        ) : null}
                                        {formDataSupplier.contact.email !== null ? (
                                            <InputComponent
                                                id="email"
                                                label="Email"
                                                register={register}
                                                value={formDataSupplier.contact.email ?? ''}
                                                onChange={handleChangeEmail}
                                                isInvalid={errors.email ?? null}
                                                isRequired={isRequiredEmail}
                                                type="email"
                                                validate={validateEmail}
                                            />
                                        ) : (
                                            <div style={{ flex: 1 }} />
                                        )}
                                    </Stack>
                                </>
                            ) : null}

                            <BigSectionTitleComponent title="Pagamento" />
                            <Stack direction="row" justify="center" align="center" spacing="30px" alignSelf="stretch">
                                <SelectComponent
                                    id="paymentMethodCountryCode"
                                    label="Codice Nazione"
                                    valueComponent={
                                        formDataSupplier.paymentMethod.countryCode
                                            ? formDataSupplier.paymentMethod.countryCode
                                            : ''
                                    }
                                    options={countries}
                                    isRequired
                                    onChangeComponent={(value) => handleChangePaymentCountryCode(value as string)}
                                    control={control}
                                    isInvalid={errors.paymentMethodCountryCode ?? null}
                                />
                                <SelectComponent
                                    id="paymentMethodCurrencyCode"
                                    label="Codice divisa"
                                    valueComponent={
                                        formDataSupplier.paymentMethod.currencyCode
                                            ? formDataSupplier.paymentMethod.currencyCode
                                            : ''
                                    }
                                    options={currencies}
                                    isRequired
                                    onChangeComponent={(value) => handleChangePaymentCurrencyCode(value as string)}
                                    control={control}
                                    isInvalid={errors.paymentMethodCurrencyCode ?? null}
                                />
                            </Stack>
                            <PaymentMethodForm
                                register={register}
                                control={control}
                                errors={errors}
                                paymentMethod={formDataSupplier.paymentMethod}
                                updateId={handlePaymentMethodId}
                                updatePaymentMethodData={handleUpdatePaymentMethod}
                                id="paymentMethod"
                                isRequired
                                showTravelAccount={false}
                                showRiba={formDataSupplier.countryTax === 'IT' ? true : false}
                                isRequiredCreditCardData={false}
                            />
                            {billingData}
                            {!vatManualValidationStatus && (
                                <Text textAlign="center" variant="subtitle" color={'red'}>
                                    Completa la validazione dei campi IVA/Codice Fiscale cliccando sul pulsante{' '}
                                    {formDataSupplier.supplierNation === SupplierNation.ITALY
                                        ? 'Convalida P. IVA/C.F.'
                                        : 'Convalida P.IVA'}
                                    .
                                </Text>
                            )}
                            <Stack
                                direction="row"
                                justify="center"
                                align="center"
                                spacing="10px"
                                alignSelf="stretch"
                                padding="20px 0"
                                marginBottom="60px"
                            >
                                <Button
                                    size="lg"
                                    variant={'outline'}
                                    borderColor={palette.brand01}
                                    color={palette.brand01}
                                    onClick={() => {
                                        navigate(-1);
                                    }}
                                >
                                    Annulla
                                </Button>
                                <Button
                                    leftIcon={<Icon as={MdDownload} />}
                                    size="lg"
                                    variant={'main'}
                                    bg={palette.brand01}
                                    type="submit"
                                    isDisabled={!vatManualValidationStatus}
                                >
                                    Salva
                                </Button>
                            </Stack>
                        </Stack>
                        <LoaderModal isVisible={isLoading} />
                        <Modal
                            closeOnOverlayClick={false}
                            isOpen={isOpen}
                            onClose={onClose}
                            motionPreset="slideInBottom"
                        >
                            <ModalOverlay />
                            <ModalContent>
                                <ModalHeader>
                                    <SmallSectionTitleComponent title="Esito salvataggio" />
                                </ModalHeader>
                                <ModalBody>
                                    <Text variant="subtitle">I dati del profilo sono stati salvati correttamente.</Text>
                                </ModalBody>

                                <ModalFooter justifyContent="center">
                                    <Button
                                        bg={palette.brand01}
                                        variant={'main'}
                                        onClick={() => {
                                            onClose();
                                            navigate(AppRoutes.HOME_PAGE);
                                        }}
                                    >
                                        Ok
                                    </Button>
                                </ModalFooter>
                            </ModalContent>
                        </Modal>
                        <ReCAPTCHA
                            size="invisible"
                            theme="dark"
                            sitekey={window.GattinoniAnagraphicForm.RECAPTCHA_PUBLIC_KEY}
                            ref={recaptchaRef}
                        />
                    </form>
                ) : (
                    <Stack flex="1" />
                )}
                <FooterComponent />
            </Stack>
        );
    }

    return null;
};

export default SupplierFormPage;
